import { useMemo } from "react";

import { useRouter } from "next/router";

import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { AmplitudeApi } from "@/api/amplitude";
import { useLeftBarData } from "@/api/left-panel/hooks";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { EVENT_CODES } from "@/constants/eventCodes";
import { VideosFavoriteResponseDto } from "@/features/favorite-videos/models";
import { LadyProfileDto } from "@/features/lady-profile/models";
import { NewsFeedResponseDto } from "@/features/newsfeed/models";
import { isAxiosExpectedError } from "@/utils/zod-utils";

import { PayVideoApi } from "../PayVideo.api";
import {
  PayVideoResponseDTO,
  PayVideoResponseDTOError,
} from "../models/PayVideo.responce.dto";

export const usePayVideo = ({
  openVideo,
}: {
  openVideo: (data: PayVideoResponseDTO) => void;
}) => {
  const { openModal, removeAll } = useOpenModal();
  const { openModal: disableBGClickModal } = useOpenModal({
    disableBackGroundClick: true,
  });

  const queryClient = useQueryClient();

  const { data, refetch } = useLeftBarData();

  const openPurchaseCoinsModal = useMemo(() => {
    if (data && "balance" in data && data.balance.isReplenished !== false) {
      return () => {
        disableBGClickModal("CREATE_PURCHASE_COINS_MODAL", {
          onClose: () => removeAll(),
        });
      };
    } else {
      return () => {
        disableBGClickModal("CREATE_ONE_TIME_PURCHASE_COINS_MODAL", {
          onClose: () => removeAll(),
        });
      };
    }
  }, [data, disableBGClickModal, removeAll]);

  const openPurchaseInfoModal = (message: string) => {
    disableBGClickModal("INFORMATION_MODAL", {
      message,
      statusReg: "success",
      buttonText: "Purchase coins",
      onOkClick: () => {
        removeAll();
        openPurchaseCoinsModal();
      },
      disableonOkClickOnCross: true,
    });
  };
  const router = useRouter();

  function updateLadyProfileCache(
    queryClient: QueryClient,
    objectId: string | string[],
    videoId: number,
    fileNameUrl: string,
  ) {
    queryClient.setQueryData<LadyProfileDto>(
      ["LADY_PROFILE", { objectId }],
      (oldData) => {
        if (!oldData) return oldData;
        const newData = { ...oldData };
        if (newData.forms.videos && Array.isArray(newData.forms.videos)) {
          newData.forms.videos = newData.forms.videos.map((video) => {
            if (video.id === videoId) {
              return {
                ...video,
                isPaid: true,
                ...(fileNameUrl && { fileNameUrl }),
              };
            }
            return video;
          });
        }
        return newData;
      },
    );
  }

  function updateNewsFeedListCache(
    queryClient: QueryClient,
    videoId: number,
    ladyId: number,
    fileNameUrl: string,
  ) {
    const newsFeedCaches = queryClient.getQueriesData({
      queryKey: ["NEWSFEED_LIST"],
    });
    newsFeedCaches.forEach(([queryKey, _]) => {
      queryClient.setQueryData<NewsFeedResponseDto>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const newData = { ...oldData };

        if (newData.itemArray && Array.isArray(newData.itemArray)) {
          newData.itemArray = newData.itemArray.map((item) => {
            if (item.ladyProfileDTO?.id == ladyId || item.userId == ladyId) {
              const newItem = { ...item };

              if (newItem.newsFeedDTOS && Array.isArray(newItem.newsFeedDTOS)) {
                newItem.newsFeedDTOS = newItem.newsFeedDTOS.map((feed) => {
                  if (
                    feed.videoGroup &&
                    feed.videoDTO &&
                    feed.videoDTO.id == videoId
                  ) {
                    return {
                      ...feed,
                      videoDTO: {
                        ...feed.videoDTO,
                        isPaid: true,
                        ...(fileNameUrl && { source: fileNameUrl }),
                      },
                    };
                  }
                  return feed;
                });
              }

              return newItem;
            }
            return item;
          });
        }

        return newData;
      });
    });
  }

  function updateFavoriteVideosCache(
    queryClient: QueryClient,
    videoId: number,
    fileNameUrl: string,
  ) {
    queryClient.setQueryData<VideosFavoriteResponseDto>(
      ["VIDEOS_LIST_FAVORITE"],
      (oldData) => {
        if (!oldData) return oldData;

        const newData = { ...oldData };

        if (newData.itemArray && Array.isArray(newData.itemArray)) {
          newData.itemArray = newData.itemArray.map((item) => {
            if (item.id === videoId) {
              return {
                ...item,
                isPaid: true,
                ...(fileNameUrl && { fileNameUrl }),
              };
            }
            return item;
          });
        }

        return newData;
      },
    );
  }
  return useMutation(PayVideoApi.payVideo, {
    onSuccess: (response) => {
      openVideo(response);

      AmplitudeApi.send({
        amplitude: "video_lady",
        page: window.location.pathname,
        favorite: true,
        coins: false,
        show: true,
      });

      refetch();

      const objectId = router.query["lady-id"];

      let videoId: number | undefined;
      let ladyId: number | undefined;
      let fileNameUrl = "";
      const dataMap = response?.requestDTO?.dataMap;

      if (dataMap && "result" in dataMap && dataMap.result) {
        videoId = dataMap.result.id;
        ladyId = dataMap.result.ladyId;
        fileNameUrl = dataMap.result.fileNameUrl || "";
      }
      if (videoId && objectId) {
        updateLadyProfileCache(queryClient, objectId, videoId, fileNameUrl);
      }

      if (videoId && ladyId) {
        updateNewsFeedListCache(queryClient, videoId, ladyId, fileNameUrl);
      }

      if (videoId) {
        updateFavoriteVideosCache(queryClient, videoId, fileNameUrl);
      }
    },

    onError: (error) => {
      let errorMessage = "Unexpected error";

      if (isAxiosExpectedError(error, PayVideoResponseDTOError)) {
        errorMessage =
          error.response?.data?.requestDTO?.dataMap?.error || errorMessage;

        if (
          error.response?.data?.requestDTO?.dataMap?.eventCode ===
          EVENT_CODES.PAYMENT_MODAL
        ) {
          openPurchaseInfoModal(
            errorMessage ||
              "Sorry, it looks like you're out of coins. Don't miss your chance, refill to watch the video",
          );
          return;
        }
      }

      openModal("INFORMATION_MODAL", {
        message: errorMessage,
        statusReg: "fail",
      });
    },
  });
};
